import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { GridTable } from '@/components/GridTable'
import { FIELD_CONTROL_CLASSNAME, FIELD_CONTROL_NATIVE_CLASSNAME } from '@/styles/formControls'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestorePoint = components['schemas']['RestorePoint']
type DriftComparison = components['schemas']['DriftComparison']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']

const KIND_WIZARD_ENTRY: Record<string, string> = {
  REPOSITORY: 'repo-wz-1',
  REF: 'ref-wz-1',
  SETTINGS_POLICY: 'settings-wz-1',
  ACCESS_INTEGRATION: 'access-wz-1',
  CI_DEPLOYMENT: 'ci-wz-1',
  REVIEW_STATE: 'review-wz-1',
  PROJECT_POLICY: 'project-wz-1',
  WORKSPACE_WEBHOOK: 'wshook-wz-1',
}

/**
 * `v-drift` — process/09-SCREEN-SPECS/v-drift.md. Both pickers offer **configuration** snapshots
 * only (`jobListRestorePoints` with `kind=CONFIG_BACKUP`) — a data-snapshot picker here would offer
 * a comparison with no configuration in it. **No value is inferred**: a row with `observation.state
 * !== 'OBSERVED'` renders the served label, never a fabricated zero. Drift **links to** the
 * relevant restore kind; it never writes (§23.7) — there is no click-to-fix.
 *
 * **`panel`/`tools`/`field`/`field-control` added W168.** The whole surface is now one real
 * `panel`; *Export comparison* now sits in a real `tools` cluster in its header, matching the
 * spec's own "`tools` · `button` — Export comparison" cell. Each picker is a real `field` wrapping
 * a real `field-control`. A fourth field is a real one, not invented: `driftCompare`/`driftExport`
 * both carry an optional `compare_workspace_id` ("Compare against another protected workspace"),
 * previously never surfaced at all — a checkbox field reveals a workspace-picker field when
 * checked, giving exactly the spec's own four `field` instances (from, to, the toggle, the
 * cross-workspace picker).
 *
 * **`legend` built for real W176 — the earlier "not built" reasoning here was wrong.** It quoted
 * `design/23-configuration-drift.md`'s own "no grade column, no severity filter and no severity
 * legend anywhere in the product" as if it forbade a legend outright. Read again against the exact
 * sentence: that is a **severity** legend (grading/colour-by-risk, removed with severity itself,
 * `OI-F10`), not the prototype's own `div.legend` on this screen, which keys the change-TYPE
 * colours (`--nochange`/`--ok`/`--info`/`--fail` → No change/Created/Modified/Deleted) — the exact
 * vocabulary `design/23` itself states ships ("what changed — created, modified, deleted, per
 * object type"). Nothing forbids it; it was never built. Built now, reusing the same colour tokens
 * the table's own `created`/`modified`/`deleted` columns already represent. Its fourth entry, "No
 * change," is kept even though nothing on this screen currently counts it — see the bar-chart
 * finding directly below for why.
 *
 * **The prototype's own horizontal stacked bar chart per object type (Prototype_v2.html
 * `#drift-rows`) is now built for real.** W176 found it genuinely blocked — `object_types[]` carried
 * `created`/`deleted`/`modified` and no "no change" count or total, and rendering the largest segment
 * in every one of the prototype's own rows off an invented denominator would have been exactly the
 * fabrication `design/23`'s own "nothing in it is inferred" rule forbids. **That was a real contract
 * gap, not a frontend one** — closed at the source: `DriftComparison.object_types[].unchanged` is a
 * new field, computed by `compare.py` from the same before/after sets `modified` is already diffed
 * from (`len(before ∩ after) - modified`), never a total minus the other three. It follows the same
 * "absent, not inferred" rule as an unobserved row — present only where `observation.state ==
 * OBSERVED`. `DriftBarChart` renders it; the three-column table stays underneath as the exact
 * numbers behind the bars.
 *
 * **`panel-menu` is not built** — the catalogue defines it as "the overflow control in a
 * panel-header," but this screen's own Actions table names exactly one control (*Export
 * comparison*), already drawn directly. An overflow menu with a single item behind it is not a
 * confidently-correct thing to invent; no second action exists to put in it.
 *
 * **The manual *Compare* button is removed and `compare` is a `useQuery`, not a `useMutation`.**
 * `Prototype_v2.html#v-drift` draws its bar chart, legend and table unconditionally — there is no
 * button in its markup at all, and both snapshot pickers already carry a live value on arrival
 * (its own most recent two). A visual-diff sweep found the app rendering none of that by default:
 * the whole comparison stayed gated behind a click, which is a genuine behavioural gap, not a
 * styling one. Fixed the same way `VHealth`'s own snapshot picker already defaults to the newest
 * snapshot (`manualJobId || points.data?.[0]`) — here the pair (`points.data[1]`, `points.data[0]`)
 * seeds `from`/`to` so a comparison is live on arrival, and changing either picker (or the
 * cross-workspace toggle) is itself the trigger, via `queryKey`, exactly as every other query-backed
 * picker in this app already refetches on a changed dependency.
 *
 * **A brand-new workspace rendered almost nothing — found live, 2026-09-25.** Drift needs two
 * configuration snapshots; with fewer than that, `fromJobId`/`toJobId` can't both resolve, so
 * `compare` (`enabled: Boolean(fromJobId && toJobId)`) never runs and every conditional block below
 * it (legend, bar chart, table) stays unrendered — the screen showed only its header, the
 * description strip and two empty "Choose a snapshot" pickers, with no stated reason. The empty
 * state below names the actual reason (zero snapshots vs. exactly one, which need different copy)
 * rather than leaving the blank space to read as broken.
 */
export function VDrift() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const [manualFromJobId, setManualFromJobId] = useState('')
  const [manualToJobId, setManualToJobId] = useState('')
  const [crossWorkspace, setCrossWorkspace] = useState(false)
  const [compareWorkspaceId, setCompareWorkspaceId] = useState('')

  const points = useQuery({
    queryKey: ['config-restore-points', orgId, workspaceId],
    // Found live, 2026-09-25 (same bug as VHealth.tsx's own fix): with no guard here, a workspace
    // switch mid-visit (`setActiveOrg`, `data/store.ts`, briefly nulls `activeWorkspaceId` while a
    // new one is chosen) fired this query with `workspace_id: null` — openapi-fetch leaves an
    // unmatched `{workspace_id}` path placeholder literally in the URL rather than substituting
    // `null`, and the API 500'd on it.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<readonly RestorePoint[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { kind: 'CONFIG_BACKUP', include_expired: true } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly RestorePoint[]
    },
  })

  // The two most recent configuration snapshots, same default-selection shape `VHealth`'s own
  // picker already uses — a manual pick always wins once made.
  const fromJobId = manualFromJobId || points.data?.[1]?.data_snapshot.job_id || ''
  const toJobId = manualToJobId || points.data?.[0]?.data_snapshot.job_id || ''

  const workspaces = useQuery({
    queryKey: ['workspaces', orgId],
    enabled: crossWorkspace,
    queryFn: async (): Promise<readonly WorkspaceSummary[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return (data.items ?? []) as unknown as readonly WorkspaceSummary[]
    },
  })

  const compare = useQuery({
    queryKey: ['drift', orgId, workspaceId, fromJobId, toJobId, crossWorkspace, compareWorkspaceId],
    enabled: Boolean(fromJobId && toJobId),
    queryFn: async (): Promise<DriftComparison> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/drift', {
        params: {
          path: { org_id: orgId, workspace_id: workspaceId },
          query: { from_snapshot_job_id: fromJobId, to_snapshot_job_id: toJobId, compare_workspace_id: crossWorkspace && compareWorkspaceId ? compareWorkspaceId : undefined },
        },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as DriftComparison
    },
  })

  const exportComparison = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/drift/export', {
        params: {
          path: { org_id: orgId, workspace_id: workspaceId },
          query: { from_snapshot_job_id: fromJobId, to_snapshot_job_id: toJobId, compare_workspace_id: crossWorkspace && compareWorkspaceId ? compareWorkspaceId : undefined },
        },
        parseAs: 'blob',
      })
      if (response.error) throw new ApiProblemError(response.error as Problem)
      const url = URL.createObjectURL(response.data as unknown as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'drift-comparison.csv'
      a.click()
      URL.revokeObjectURL(url)
    },
  })

  const problem = compare.error instanceof ApiProblemError ? compare.error.problem : undefined
  const options = points.data ?? []
  const snapshotCount = points.data?.length ?? 0
  const notEnoughSnapshotsYet = points.isSuccess && snapshotCount < 2

  return (
    <div data-scr={unitId('v-drift')}>
      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
        <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
          <h1 className="m-0 text-section font-semibold flex-1">Configuration changes by object type</h1>
          <div data-cmp="tools">
            <Button data-act={actionId('drift.export')} ghost disabled={!compare.data || exportComparison.isPending} onClick={() => exportComparison.mutate()}>
              Export comparison
            </Button>
          </div>
        </div>

        <Strip>
          Compare configuration between two configuration snapshots, in one workspace or across two you protect. Every comparable configuration object type is compared in one list —
          there are no dimension tabs.
        </Strip>

        {notEnoughSnapshotsYet ? (
          <div className="py-16 px-8 text-center">
            <h3 className="m-0 mb-3 text-body-lg font-semibold">{snapshotCount === 0 ? 'No configuration backups yet' : 'Only one configuration backup so far'}</h3>
            <p className="m-0 mb-7 mx-auto text-body text-tx2" style={{ maxWidth: 480 }}>
              {snapshotCount === 0
                ? 'Configuration drift compares two configuration backups over time, and none has completed for this workspace yet. Once your first two have run, what changed between them will appear here.'
                : 'Configuration drift compares two configuration backups over time, and only one exists so far. Check back after the next one completes.'}
            </p>
            <Button data-act={undefined} onClick={() => navigate('/backup')}>
              Back up now
            </Button>
          </div>
        ) : (
        <div className="grid gap-5 mt-6 mb-6" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div data-cmp="field">
            <label className="block text-small font-semibold text-tx2">
              Source configuration snapshot
              <div data-cmp="field-control" className={`mt-2 ${FIELD_CONTROL_CLASSNAME}`}>
                <select value={fromJobId} onChange={(e) => setManualFromJobId(e.target.value)} className={FIELD_CONTROL_NATIVE_CLASSNAME}>
                  <option value="">Choose a snapshot</option>
                  {options.map((p) => (
                    <option key={p.data_snapshot.job_id} value={p.data_snapshot.job_id}>
                      {p.started_at ? p.started_at.display : p.data_snapshot.job_id}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          </div>
          <div data-cmp="field">
            <label className="block text-small font-semibold text-tx2">
              Destination configuration snapshot
              <div data-cmp="field-control" className={`mt-2 ${FIELD_CONTROL_CLASSNAME}`}>
                <select value={toJobId} onChange={(e) => setManualToJobId(e.target.value)} className={FIELD_CONTROL_NATIVE_CLASSNAME}>
                  <option value="">Choose a snapshot</option>
                  {options.map((p) => (
                    <option key={p.data_snapshot.job_id} value={p.data_snapshot.job_id}>
                      {p.started_at ? p.started_at.display : p.data_snapshot.job_id}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          </div>
          <div data-cmp="field">
            <label className="flex items-center gap-3 text-small font-semibold text-tx2">
              <div data-cmp="field-control">
                <input
                  type="checkbox"
                  checked={crossWorkspace}
                  onChange={(e) => {
                    setCrossWorkspace(e.target.checked)
                    if (!e.target.checked) setCompareWorkspaceId('')
                  }}
                />
              </div>
              Compare against another protected workspace
            </label>
          </div>
          {crossWorkspace && (
            <div data-cmp="field">
              <label className="block text-small font-semibold text-tx2">
                Other workspace
                <div data-cmp="field-control" className={`mt-2 ${FIELD_CONTROL_CLASSNAME}`}>
                  <select value={compareWorkspaceId} onChange={(e) => setCompareWorkspaceId(e.target.value)} className={FIELD_CONTROL_NATIVE_CLASSNAME}>
                    <option value="">Choose a workspace</option>
                    {(workspaces.data ?? [])
                      .filter((w) => w.workspace_id !== workspaceId)
                      .map((w) => (
                        <option key={w.workspace_id} value={w.workspace_id}>
                          {w.name}
                        </option>
                      ))}
                  </select>
                </div>
              </label>
            </div>
          )}
        </div>
        )}

        {problem && <div className="mt-6">{isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>}</div>}

        {compare.data && (
          <div data-cmp="legend" className="flex justify-end gap-5 text-small text-tx2 mt-7">
            <span className="flex items-center gap-2">
              <i className="inline-block w-3 h-3 rounded-1 bg-nochange" />
              No change
            </span>
            <span className="flex items-center gap-2">
              <i className="inline-block w-3 h-3 rounded-1 bg-ok" />
              Created
            </span>
            <span className="flex items-center gap-2">
              <i className="inline-block w-3 h-3 rounded-1 bg-info" />
              Modified
            </span>
            <span className="flex items-center gap-2">
              <i className="inline-block w-3 h-3 rounded-1 bg-fail" />
              Deleted
            </span>
          </div>
        )}

        {compare.data && (
          <div className="mt-5">
            <DriftBarChart
              rows={compare.data.object_types.map((row) => ({
                objectType: row.object_type,
                observed: row.observation.state === 'OBSERVED',
                unchanged: row.unchanged,
                created: row.created,
                modified: row.modified,
                deleted: row.deleted,
              }))}
            />
          </div>
        )}

        {compare.data && (
          <div className="mt-4">
            <GridTable
              columns={[
                { key: 'objectType', header: 'Object type', render: (row) => row.object_type },
                {
                  key: 'created',
                  header: 'Created',
                  colSpan: (row) => (row.observation.state === 'OBSERVED' ? 1 : 3),
                  render: (row) => (row.observation.state === 'OBSERVED' ? (row.created ?? 0) : <span className="text-muted">{row.observation.display ?? 'Not re-observed in this window'}</span>),
                },
                { key: 'deleted', header: 'Deleted', render: (row) => (row.observation.state === 'OBSERVED' ? (row.deleted ?? 0) : null) },
                { key: 'modified', header: 'Modified', render: (row) => (row.observation.state === 'OBSERVED' ? (row.modified ?? 0) : null) },
                {
                  key: 'restore',
                  header: '',
                  render: (row) =>
                    row.restore_kind && (
                      <Button data-act={actionId(`nav.${KIND_WIZARD_ENTRY[row.restore_kind]}`)} ghost onClick={() => navigate(`/${KIND_WIZARD_ENTRY[row.restore_kind!]}`)}>
                        Restore
                      </Button>
                    ),
                },
              ]}
              rows={compare.data.object_types}
              getRowKey={(row) => row.object_type}
            />
          </div>
        )}
      </div>
    </div>
  )
}

interface DriftBarRow {
  readonly objectType: string
  readonly observed: boolean
  readonly unchanged?: number
  readonly created?: number
  readonly modified?: number
  readonly deleted?: number
}

/**
 * `div.hbar` (`Prototype_v2.html` `#drift-rows`) → `bar-row`, `div.track` → `bar-track`,
 * `span.num` → `bar-value` (`process/08-COMPONENT-CATALOGUE.md`) — the horizontal stacked bar per
 * object type. Four segments (`div.sg`, uncatalogued — "carries no value") proportional to a
 * shared denominator (the widest row's own total), same as the prototype's `max`. A row whose
 * `observation.state !== 'OBSERVED'` draws no bar at all — rendering a zero-width one would read
 * as *zero changes*, exactly the inference §23.2a forbids; the full stated reason already renders
 * once, in the table beneath.
 */
function DriftBarChart({ rows }: { readonly rows: readonly DriftBarRow[] }) {
  const totals = rows.map((r) => (r.unchanged ?? 0) + (r.created ?? 0) + (r.modified ?? 0) + (r.deleted ?? 0))
  const max = Math.max(1, ...totals)

  return (
    <div>
      {rows.map((row, i) => (
        <div key={row.objectType} data-cmp="bar-row" className="grid items-center gap-3 py-1 text-small" style={{ gridTemplateColumns: '190px 1fr' }}>
          <span className="truncate">{row.objectType}</span>
          {row.observed ? (
            <div data-cmp="bar-track" className="flex items-center h-[14px]">
              <div className="h-[14px] bg-nochange" style={{ width: `${((row.unchanged ?? 0) / max) * 100}%` }} />
              <div className="h-[14px] bg-ok" style={{ width: `${((row.created ?? 0) / max) * 100}%` }} />
              <div className="h-[14px] bg-info" style={{ width: `${((row.modified ?? 0) / max) * 100}%` }} />
              <div className="h-[14px] bg-fail" style={{ width: `${((row.deleted ?? 0) / max) * 100}%` }} />
              <span data-cmp="bar-value" className="text-micro text-tx2 pl-2 whitespace-nowrap">
                {totals[i]}
              </span>
            </div>
          ) : (
            <span className="text-micro text-tx2">—</span>
          )}
        </div>
      ))}
      <div className="text-center text-micro text-tx2 font-semibold mt-2">Count</div>
    </div>
  )
}
